import React, {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button} from 'primereact/button';
import {Checkbox} from 'primereact/checkbox';
import {FileUpload} from 'primereact/fileupload';
import {Dropdown} from 'primereact/dropdown';
import {InputText} from 'primereact/inputtext';
import {ProgressBar} from 'primereact/progressbar';
import {RadioButton} from 'primereact/radiobutton';
import {Field, useForm, useStore} from '@tanstack/react-form';
import axios from '../../transport/axios';
import styles from './styles.module.css';
import {useAuth} from '../../providers/auth/auth-provider';
import {useMessage} from '../../providers/message/message-provider';
import {fetch} from '../../shared/api/v1';
import type {components} from '../../shared/api/v1/openapi.types';
import {maxLength} from '../../utils/validation';
import {Connection, FileGroups, FILE_KEYS, FILE_LABELS, FIELD_LABELS, FormValues, draftPayload, validateValues} from './connection-form-model';
import {isConnectionDraft} from '../../constants/connection';
import {
  CONNECTION_LOAD_NATURE_OPTIONS,
  CONNECTION_METHOD_OPTIONS,
  CONNECTION_REASON_OPTIONS,
  CONNECTION_RELIABILITY_OPTIONS,
} from '../../constants/connection';

type ConnectionPointPowerDto = components['schemas']['ConnectionPointPowerDto'];
type ConnectionEntity = components['schemas']['Connection'];

type ConnectionFormProps = {
  connectionId: string;
  connection: Connection | null;
  onConnectionUpdated: (connection: Connection) => void;
  onDirtyChange: (dirty: boolean) => void;
  onBusyChange: (busy: boolean) => void;
};

const TYPE_OPTIONS = [
  {
    label:
      'Заявка юридического лица (ИП), физического лица на временное присоединение энергопринимающих устройств',
    value: 1,
  },
  {
    label:
      'Заявка юридического лица (ИП), физического лица на присоединение энергопринимающих устройств',
    value: 2,
  },
  {
    label:
      'Заявка юридического лица (ИП), физического лица на присоединение по одному источнику электроснабжения энергопринимающих устройств с максимальной мощностью до 150 кВт включительно',
    value: 3,
  },
  {
    label:
      'Заявка физического лица на присоединение по одному источнику электроснабжения энергопринимающих устройств с максимальной мощностью до 15 кВт включительно (для бытовых нужд)',
    value: 4,
  },
];

const shouldShow = (type: number, ...types: number[]) => types.includes(type);
const MAX_FILE_SIZE_BYTES = 3 * 1024 * 1024;
// Лимит совпадает с FilesInterceptor('files', 10) на бэкенде: 11-й файл даёт 400.
const MAX_FILES_TOTAL = 10;
const MAX_FILES_TOTAL_MESSAGE = 'К заявке можно приложить не более 10 файлов.';
const REQUIRED_FILE_KEYS = ['routes', 'ownership'];
const ALWAYS_VISIBLE_FILE_KEYS = ['routes', 'ownership', 'misc'];
const TYPE_234_FILE_KEYS = ['plan', 'energySchema', 'ukAgreement'];
const TYPE_1_FILE_KEYS = ['contractInfoFile'];

const visibleFileKeys = (type: number): string[] => {
  const keys = [...ALWAYS_VISIBLE_FILE_KEYS];
  if (shouldShow(type, 2, 3, 4)) {
    keys.push(...TYPE_234_FILE_KEYS);
  }
  if (shouldShow(type, 1)) {
    keys.push(...TYPE_1_FILE_KEYS);
  }
  return keys;
};

const countFiles = (files: Record<string, File[]>) =>
  Object.values(files).reduce((sum, group) => sum + (group?.length ?? 0), 0);

const fileIdentity = (file: File) => `${file.name}|${file.size}|${file.lastModified}`;

const dedupeFiles = (list: File[]) => {
  const seen = new Set<string>();
  const result: File[] = [];
  list.forEach((file) => {
    const id = fileIdentity(file);
    if (seen.has(id)) {
      return;
    }
    seen.add(id);
    result.push(file);
  });
  return result;
};

const EMPTY_FORM_VALUES: FormValues = {
  type: 0, devNames: '', region: '', address: '', maxPower: '', powerOwened: '',
  powerBefore: '', voltage: '', pointsNum: '', voltage1: '', voltage2: '', voltage3: '',
  pointsC: [], pointsB: [], loadNature: undefined, loadNatureFull: '', transNum: '',
  genNum: '', generator: '', armor: '', armorValue: '', reliability: undefined,
  level1: '', level2: '', level3: '', tempDates: '', dates: '', plans: '', offers: '',
  reason: undefined, method: undefined, personalAgreement: false, contractInfo: '', timeline: '',
};

export const ConnectionForm = ({connectionId, connection, onConnectionUpdated, onDirtyChange, onBusyChange}: ConnectionFormProps) => {
  const isNew = connectionId === 'new';
  const isDraft = isNew || isConnectionDraft(connection?.status);
  const [files, setFiles] = useState<Record<string, File[]>>({});
  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});
  const fileUploadRefs = useRef<Record<string, FileUpload | null>>({});
  const fileUploadCallbacks = useRef<Record<string, (element: FileUpload | null) => void>>({});
  const selectedFilesRef = useRef(files);
  selectedFilesRef.current = files;
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [existingFiles, setExistingFiles] = useState<ConnectionEntity['files']>(connection?.files ?? []);
  const [fileGroups, setFileGroups] = useState<FileGroups>(connection?.fileGroups ?? {});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [savedValues, setSavedValues] = useState('');
  const [savedGroups, setSavedGroups] = useState(JSON.stringify(connection?.fileGroups ?? {}));
  const actionPending = useRef(false);
  const uploadedFiles = useRef(new Map<File, number>());
  const currentConnectionId = useRef(connection?.id);
  const appliedConnection = useRef<Connection | null | undefined>(undefined);
  const navigate = useNavigate();
  const {token} = useAuth();
  const {showError, showMessage} = useMessage();
  const requiredMark = <span className={`${styles.requiredMark} p-error`}>*</span>;
  const existingCount = new Set(
    Object.values(fileGroups).flat().filter((id): id is number => typeof id === 'number'),
  ).size;
  const isBudgetExhausted = countFiles(files) + existingCount >= MAX_FILES_TOTAL;
  const getFileUploadClass = (key: string) =>
    `connection-file-upload${fileErrors[key] ? ' p-invalid' : ''}${isBudgetExhausted ? ' is-select-disabled' : ''}`;
  const renderFileError = (key: string) =>
    fileErrors[key] ? <small className="p-error">{fileErrors[key]}</small> : null;
  const setFileUploadRef = (key: string) => {
    if (!fileUploadCallbacks.current[key]) {
      fileUploadCallbacks.current[key] = (element) => {
        fileUploadRefs.current[key] = element;
        if (element) {
          const selected = selectedFilesRef.current[key] ?? [];
          const rendered = element.getFiles();
          if (rendered.length !== selected.length || rendered.some((file, index) => file !== selected[index])) {
            element.setFiles(selected);
          }
        }
      };
    }
    return fileUploadCallbacks.current[key];
  };

  const downloadExistingFile = async (file: ConnectionEntity['files'][number]) => {
    if (!token) {
      showError({ message: 'Нет авторизации. Пожалуйста, войдите в аккаунт.' });
      return;
    }
    try {
      const response = await axios.get(file.url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.name || `file-${file.id}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      const message = extractErrorMessage(error);
      showError({
        message: message ?? 'Ошибка при загрузке файла',
      });
    }
  };

  const extractErrorMessage = (error: unknown) => {
    if (!error || typeof error !== 'object') {
      return undefined;
    }
    const maybeMessage = (error as { message?: string }).message;
    if (maybeMessage) {
      return maybeMessage;
    }
    const nestedMessage = (error as { error?: { message?: string } }).error?.message;
    if (nestedMessage) {
      return nestedMessage;
    }
    return (error as { data?: { message?: string } }).data?.message;
  };

  const applyFileErrors = (key: string) => {
    setFileErrors((prevErrors) => {
      if (!prevErrors[key]) {
        return prevErrors;
      }
      const { [key]: _removed, ...rest } = prevErrors;
      return rest;
    });
  };

  // PrimeReact передаёт в onSelect накопительный список файлов поля, а не только
  // что добавленные. Поэтому состояние перезаписываем: дополнение давало
  // дубликаты, из-за которых в запрос уходило не то, что видно на экране.
  const syncFieldFiles = (key: string, widgetFiles: File[]) => {
    const unique = dedupeFiles(widgetFiles);
    const usedByOthers = countFiles(files) + existingCount - (files[key]?.length ?? 0);
    const budget = Math.max(MAX_FILES_TOTAL - usedByOthers, 0);
    const accepted = unique.slice(0, budget);
    const rejected = unique.length - accepted.length;

    setFiles((prev) => ({
      ...prev,
      [key]: accepted,
    }));
    // Список в виджете приводим к тому, что реально будет отправлено, иначе
    // потребитель видит файл, которого нет в запросе.
    fileUploadRefs.current[key]?.setFiles(accepted);

    if (rejected > 0) {
      showError({
        message: accepted.length === 0
          ? MAX_FILES_TOTAL_MESSAGE
          : `${MAX_FILES_TOTAL_MESSAGE} Добавлено только ${accepted.length} из ${unique.length}.`,
      });
    }
    applyFileErrors(key);
  };
  const removeFile = (key: string, removed: File) => {
    const current = files[key] ?? [];
    const index = current.findIndex(
      (file) => fileIdentity(file) === fileIdentity(removed),
    );
    if (index < 0) {
      return;
    }
    // Удаляем ровно одну запись: виджет уже убрал файл из своего списка, и
    // дубликатов в состоянии больше нет.
    const next = [...current.slice(0, index), ...current.slice(index + 1)];
    setFiles((prev) => ({
      ...prev,
      [key]: next,
    }));
    applyFileErrors(key);
  };
  const clearFiles = (key: string) => {
    setFiles((prev) => ({
      ...prev,
      [key]: [],
    }));
    applyFileErrors(key);
  };
  const extractFileIds = (body: unknown): number[] => {
    if (Array.isArray((body as { fileIds?: number[] })?.fileIds)) {
      return (body as { fileIds: number[] }).fileIds;
    }
    if (Array.isArray(body)) {
      return body as number[];
    }
    if (Array.isArray((body as { ids?: number[] })?.ids)) {
      return (body as { ids: number[] }).ids;
    }
    if (Array.isArray((body as { data?: number[] })?.data)) {
      return (body as { data: number[] }).data;
    }
    return [];
  };

  const uploadFiles = async (): Promise<FileGroups> => {
    if (!token) {
      throw new Error('Нет авторизации. Пожалуйста, войдите в аккаунт.');
    }
    if (countFiles(files) + existingCount > MAX_FILES_TOTAL) {
      throw new Error(MAX_FILES_TOTAL_MESSAGE);
    }
    const groups: FileGroups = {...fileGroups};
    setIsUploadingFiles(true);
    try {
      for (const key of FILE_KEYS) {
        const ids = [...(groups[key] ?? [])];
        for (const file of files[key] ?? []) {
          let id = uploadedFiles.current.get(file);
          if (!id) {
            const body = new FormData();
            body.append('files', file);
            // Swagger старого upload не описывает multipart-тело и ответ.
            const result = await fetch.POST('/upload/consumer', {
              body: body as never,
              bodySerializer: () => body,
              headers: {Authorization: `Bearer ${token}`},
            });
            if (!result.response.ok) {
              throw new Error(extractErrorMessage(result.error) ?? 'Не удалось загрузить файлы. Попробуйте ещё раз.');
            }
            id = extractFileIds(result.data)[0];
            if (!id) throw new Error('Сервер не вернул ID загруженного файла.');
            // Сохраняем каждый успешный ID: повтор после частичного сбоя не загружает его заново.
            uploadedFiles.current.set(file, id);
          }
          ids.push(id);
        }
        groups[key] = Array.from(new Set(ids));
      }
      return groups;
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const handleBeforeSelect = () => {
    if (!isBudgetExhausted) {
      return;
    }
    showError({ message: MAX_FILES_TOTAL_MESSAGE });
    // PrimeReact проверяет строгое равенство false и отменяет выбор целиком,
    // поэтому список виджета не расходится с состоянием.
    return false;
  };

  const renderFileUpload = (key: typeof FILE_KEYS[number], chooseLabel: string) => (
    <div id={`files-${key}`} className="flex flex-column gap-2">
      {(fileGroups[key] ?? []).map((id) => {
        const file = existingFiles.find((item) => item.id === id);
        return (
          <div key={id} className="flex align-items-center gap-2">
            <Button type="button" text icon="pi pi-download" label={file?.name ?? `Файл №${id}`}
              onClick={() => file && downloadExistingFile(file)} />
            <Button type="button" text icon="pi pi-times" aria-label={`Открепить ${file?.name ?? id}`}
              onClick={() => setFileGroups((previous) => ({...previous, [key]: (previous[key] ?? []).filter((item) => item !== id)}))} />
          </div>
        );
      })}
    <FileUpload
      ref={setFileUploadRef(key)}
      name={key}
      mode="advanced"
      className={getFileUploadClass(key)}
      chooseLabel={chooseLabel}
      accept="image/png, image/jpeg, image/jpg, application/pdf"
      maxFileSize={MAX_FILE_SIZE_BYTES}
      invalidFileSizeMessageSummary="Слишком большой файл"
      invalidFileSizeMessageDetail="Максимальный размер файла: 3 МБ."
      customUpload
      headerTemplate={(options) => <div className="p-fileupload-buttonbar">{options.chooseButton}{options.cancelButton}</div>}
      multiple
      onBeforeSelect={handleBeforeSelect}
      onSelect={(e) => syncFieldFiles(key, e.files as File[])}
      onRemove={(e) => removeFile(key, e.file as File)}
      onClear={() => clearFiles(key)}
    />
    </div>
  );

  const mapConnectionToFormValues = (connection: ConnectionEntity): FormValues => ({
    type: connection.type ?? 0,
    devNames: connection.devNames ?? '',
    region: connection.region ?? '',
    address: connection.address ?? '',
    maxPower: connection.maxPower ?? '',
    powerOwened: connection.powerOwened ?? '',
    powerBefore: connection.powerBefore ?? '',
    voltage: connection.voltage ?? '',
    pointsNum: connection.pointsNum ?? '',
    voltage1: connection.voltage1 ?? '',
    voltage2: connection.voltage2 ?? '',
    voltage3: connection.voltage3 ?? '',
    pointsC: connection.pointsC ?? [],
    pointsB: connection.pointsB ?? [],
    loadNature: connection.loadNature ?? undefined,
    loadNatureFull: connection.loadNatureFull ?? '',
    transNum: connection.transNum ?? '',
    genNum: connection.genNum ?? '',
    generator: connection.generator ?? '',
    armor: connection.armor ?? '',
    armorValue: connection.armorValue ?? '',
    reliability: connection.reliability ?? undefined,
    level1: connection.level1 ?? '',
    level2: connection.level2 ?? '',
    level3: connection.level3 ?? '',
    tempDates: connection.tempDates ?? '',
    dates: connection.dates ?? '',
    plans: connection.plans ?? '',
    offers: connection.offers ?? '',
    reason: connection.reason ?? undefined,
    method: connection.method ?? undefined,
    personalAgreement: connection.personalAgreement ?? false,
    contractInfo: connection.contractInfo ?? '',
    timeline: connection.timeline ?? '',
    date: connection.date ?? undefined,
  });

  const form = useForm({
    defaultValues: EMPTY_FORM_VALUES,
  });

  const Subscribe = form.Subscribe;
  const currentValues = useStore<typeof form.state, FormValues>(form.store, (state) => state.values);

  useEffect(() => {
    if (appliedConnection.current === connection) {
      return;
    }
    appliedConnection.current = connection;
    const values = connection ? mapConnectionToFormValues(connection) : EMPTY_FORM_VALUES;
    (Object.keys(values) as Array<keyof FormValues>).forEach((key) => {
      form.setFieldValue(key, values[key]);
    });
    setSavedValues(JSON.stringify(values));
    setExistingFiles(connection?.files ?? []);
    setFileGroups(connection?.fileGroups ?? {});
    setSavedGroups(JSON.stringify(connection?.fileGroups ?? {}));
    currentConnectionId.current = connection?.id;
  }, [connection, form]);

  useEffect(() => {
    onDirtyChange(JSON.stringify(currentValues) !== savedValues || countFiles(files) > 0 || JSON.stringify(fileGroups) !== savedGroups);
  }, [currentValues, files, fileGroups, savedValues, savedGroups, onDirtyChange]);

  useEffect(() => {
    setFieldErrors({});
  }, [currentValues]);

  const acceptConnection = (data: Connection) => {
    currentConnectionId.current = data.id;
    setFiles({});
    Object.values(fileUploadRefs.current).forEach((widget) => widget?.clear());
    uploadedFiles.current.clear();
    onDirtyChange(false);
    onConnectionUpdated(data);
  };

  const readResult = async (result: {data?: Connection; error?: unknown; response: Response}): Promise<Connection> => {
    if (result.response.ok && result.data) return result.data;
    const error = result.error as {errors?: Array<{field: string; message: string}>} | undefined;
    const fields: Record<string, string> = {};
    const groups: Record<string, string> = {};
    error?.errors?.forEach(({field, message}) => {
      if (field.startsWith('fileGroups.')) groups[field.split('.')[1]] = message;
      else fields[field] = message;
    });
    setFieldErrors(fields);
    setFileErrors(groups);
    if (result.response.status === 409 && currentConnectionId.current && token) {
      const actual = await fetch.GET('/connections/{id}', {
        params: {path: {id: currentConnectionId.current}}, headers: {Authorization: `Bearer ${token}`},
      });
      if (actual.data) acceptConnection(actual.data);
      throw new Error('Заявка уже отправлена. Обновите страницу.');
    }
    if (result.response.status === 401) throw new Error('Сессия истекла. Пожалуйста, войдите в аккаунт.');
    if (result.response.status === 403 || result.response.status === 404) throw new Error('Заявка не найдена или недоступна.');
    throw new Error(extractErrorMessage(result.error) ?? (isDraft ? 'Не удалось сохранить черновик. Попробуйте ещё раз.' : 'Не удалось обновить заявление. Попробуйте ещё раз.'));
  };

  const save = async (action: 'draft' | 'submit') => {
    if (actionPending.current) return;
    const values = form.state.values;
    const errors = validateValues(values, action === 'submit');
    const documentErrors: Record<string, string> = {};
    if (action === 'submit' && isDraft) {
      REQUIRED_FILE_KEYS.forEach((key) => {
        if (!(files[key]?.length || fileGroups[key as keyof FileGroups]?.length)) documentErrors[key] = 'Поле обязательно для заполнения.';
      });
    }
    for (const [key, selected] of Object.entries(files)) {
      if (selected.some((file) => file.size > MAX_FILE_SIZE_BYTES || !['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'].includes(file.type))) {
        documentErrors[key] = 'Допустимы PDF, PNG, JPEG, JPG размером до 3 МиБ.';
      }
    }
    setFieldErrors(errors);
    setFileErrors(documentErrors);
    if (Object.keys(errors).length || Object.keys(documentErrors).length) {
      showError({message: action === 'submit' ? 'Обязательные поля нужно заполнить перед отправкой заявления.' : 'Проверьте заполненные поля и выбранные файлы.'});
      return;
    }
    if (!token) {
      showError({message: 'Нет авторизации. Пожалуйста, войдите в аккаунт.'});
      return;
    }
    actionPending.current = true;
    setIsLoading(true);
    onBusyChange(true);
    const headers = {Authorization: `Bearer ${token}`};
    try {
      if (!isDraft) {
        const result = await fetch.PATCH('/connections/{id}', {
          params: {path: {id: Number(connectionId)}}, body: values, headers,
        });
        acceptConnection(await readResult(result));
        showMessage({message: 'Заявка успешно обновлена'});
        return;
      }
      const groups = await uploadFiles();
      if (action === 'submit' && isNew && !currentConnectionId.current) {
        const result = await fetch.POST('/connections', {
          body: {...values, fileIds: Object.values(groups).flat()}, headers,
        });
        acceptConnection(await readResult(result));
        showMessage({message: 'Заявление отправлено'});
        navigate('/cabinet', {replace: true});
        return;
      }
      const body = draftPayload(values, groups);
      const result = currentConnectionId.current
        ? await fetch.PATCH('/connections/{id}/draft', {params: {path: {id: currentConnectionId.current}}, body, headers})
        : await fetch.POST('/connections/drafts', {body, headers});
      const saved = await readResult(result);
      acceptConnection(saved);
      if (action === 'submit') {
        const submitted = await fetch.POST('/connections/{id}/submit', {params: {path: {id: saved.id}}, body: {}, headers});
        acceptConnection(await readResult(submitted));
        showMessage({message: 'Заявление отправлено'});
      } else {
        showMessage({message: 'Черновик сохранён'});
      }
      navigate('/cabinet', {replace: true});
    } catch (error) {
      showError({message: error instanceof Error ? error.message : 'Не удалось сохранить черновик. Попробуйте ещё раз.'});
    } finally {
      actionPending.current = false;
      setIsLoading(false);
      onBusyChange(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void save('submit');
      }}
    >
      <div className="relative">
        {(isLoading || isUploadingFiles) ? (
          <div className="mb-2">
            <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
          </div>
        ) : null}
        <fieldset className="p-0 m-0 border-none" disabled={isLoading || isUploadingFiles}>
          <div className="flex flex-column gap-3">
        <div className="flex flex-column gap-2">
          <span className={`${styles.requiredLabel} font-semibold`}>
            Тип заявления {requiredMark}
          </span>
          <div className="flex flex-column gap-3">
            {TYPE_OPTIONS.map((option) => (
              <Field
                key={option.value}
                form={form}
                name="type"
              >
                {(field) => (
                  <div className="flex align-items-center gap-2">
                    <RadioButton
                      inputId={`type-${option.value}`}
                      value={option.value}
                      checked={field.state.value === option.value}
                      disabled={!isDraft}
                      onChange={() => {
                        field.handleChange(option.value);
                      }}
                    />
                    <label htmlFor={`type-${option.value}`}>{option.label}</label>
                  </div>
                )}
              </Field>
            ))}
            {isDraft ? <Button type="button" text label="Снять выбор типа" onClick={() => form.setFieldValue('type', 0)} /> : null}
          </div>
        </div>

        <Subscribe<number> selector={(state) => state.values.type}>
          {(type) => {
            if (!shouldShow(type, 1, 2, 3, 4)) {
              return null;
            }

            return (
              <React.Fragment>
              <Field
                form={form}
                name="devNames"
                validators={{
                  onChange: ({value}) => maxLength(value, 99),
                }}
              >
                {(field) => (
                  <div className="flex flex-column gap-2">
                    <label htmlFor={field.name} className={styles.requiredLabel}>
                      Наименование энергопринимающих устройств, объекта {requiredMark}
                    </label>
                    <InputText
                      id={field.name}
                      value={field.state.value ?? ''}
                      onBlur={field.handleBlur}
                      className="w-full"
                      maxLength={99}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                      <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                    ) : null}
                  </div>
                )}
              </Field>

              <Field
                form={form}
                name="region"
                validators={{
                  onChange: ({value}) => maxLength(value, 99),
                }}
              >
                {(field) => (
                  <div className="flex flex-column gap-2">
                    <label htmlFor={field.name} className={styles.requiredLabel}>
                      Регион местонахождения энергопринимающего устройства {requiredMark}
                    </label>
                    <InputText
                      id={field.name}
                      value={field.state.value ?? ''}
                      onBlur={field.handleBlur}
                      className="w-full"
                      maxLength={99}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                      <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                    ) : null}
                  </div>
                )}
              </Field>

              <Field
                form={form}
                name="address"
                validators={{
                  onChange: ({value}) => maxLength(value, 199),
                }}
              >
                {(field) => (
                  <div className="flex flex-column gap-2">
                    <label htmlFor={field.name} className={styles.requiredLabel}>
                      Адрес местонахождения энергопринимающих устройств {requiredMark}
                    </label>
                    <InputText
                      id={field.name}
                      value={field.state.value ?? ''}
                      onBlur={field.handleBlur}
                      className="w-full"
                      maxLength={199}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                      <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                    ) : null}
                  </div>
                )}
              </Field>

              <div className="flex flex-column gap-2">
                <span className="font-semibold">Максимальная мощность суммарная</span>
                <div className="flex flex-column md:flex-row gap-2">
                  <div className="flex-1">
                    <Field
                      form={form}
                      name="maxPower"
                      validators={{
                        onChange: ({value}) => maxLength(value, 9),
                      }}
                    >
                      {(field) => (
                        <div className="flex flex-column gap-2">
                          <label htmlFor={field.name} className={styles.requiredLabel}>
                            Максимальная мощность, кВт {requiredMark}
                          </label>
                          <InputText
                            id={field.name}
                            value={field.state.value ?? ''}
                            onBlur={field.handleBlur}
                            className="w-full"
                            maxLength={9}
                            onChange={(e) => field.handleChange(e.target.value)}
                          />
                          <small className="text-color-secondary">
                            Присоединяемая + ранее присоединенная мощность энергопринимающих устройств
                          </small>
                          {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                            <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                          ) : null}
                        </div>
                      )}
                    </Field>
                  </div>
                  <div className="flex-1">
                    <Field
                      form={form}
                      name="voltage1"
                      validators={{
                        onChange: ({value}) => maxLength(value, 9),
                      }}
                    >
                      {(field) => (
                        <div className="flex flex-column gap-2">
                          <label htmlFor={field.name}>Уровень напряжения, кВ</label>
                          <InputText
                            id={field.name}
                            value={field.state.value ?? ''}
                            onBlur={field.handleBlur}
                            className="w-full"
                            maxLength={9}
                            onChange={(e) => field.handleChange(e.target.value)}
                          />
                          {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                            <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                          ) : null}
                        </div>
                      )}
                    </Field>
                  </div>
                </div>
              </div>

              {shouldShow(type, 2, 3, 4) ? (
                <div className="flex flex-column gap-2">
                  <span className="font-semibold">Присоединяемая мощность</span>
                  <div className="flex flex-column md:flex-row gap-2">
                    <div className="flex-1">
                      <Field
                        form={form}
                        name="powerOwened"
                        validators={{
                          onChange: ({value}) => maxLength(value, 9),
                        }}
                      >
                        {(field) => (
                          <div className="flex flex-column gap-2">
                            <label htmlFor={field.name}>Присоединяемая мощность, кВА</label>
                            <InputText
                              id={field.name}
                              value={field.state.value ?? ''}
                              onBlur={field.handleBlur}
                              className="w-full"
                              maxLength={9}
                              onChange={(e) => field.handleChange(e.target.value)}
                            />
                            {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                              <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                            ) : null}
                          </div>
                        )}
                      </Field>
                    </div>
                    <div className="flex-1">
                      <Field
                        form={form}
                        name="voltage2"
                        validators={{
                          onChange: ({value}) => maxLength(value, 9),
                        }}
                      >
                        {(field) => (
                          <div className="flex flex-column gap-2">
                            <label htmlFor={field.name}>Уровень напряжения, кВ</label>
                            <InputText
                              id={field.name}
                              value={field.state.value ?? ''}
                              onBlur={field.handleBlur}
                              className="w-full"
                              maxLength={9}
                              onChange={(e) => field.handleChange(e.target.value)}
                            />
                            {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                              <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                            ) : null}
                          </div>
                        )}
                      </Field>
                    </div>
                  </div>

                  {shouldShow(type, 2) ? (
                    <Field form={form} name="pointsC">
                      {(field) => (
                        <div className="flex flex-column gap-2">
                          <div className="flex align-items-center justify-content-between">
                            <span>Точки присоединения</span>
                            <Button
                              type="button"
                              size="small"
                              onClick={() =>
                                field.handleChange([...(field.state.value ?? []), {name: '', value: ''}])
                              }
                            >
                              Добавить точку
                            </Button>
                          </div>
                          <small className="text-color-secondary">
                            Количество точек присоединения с указанием технических параметров элементов
                            энергопринимающих устройств
                          </small>
                          {(field.state.value ?? []).map((point, index) => (
                            <div key={index} className="flex flex-column md:flex-row gap-2">
                              <InputText
                                value={point?.name ?? ''}
                                className="w-full"
                                placeholder="Наименование"
                                maxLength={99}
                                onChange={(e) => {
                                  const updated = [...(field.state.value ?? [])];
                                  updated[index] = { ...updated[index], name: e.target.value };
                                  field.handleChange(updated);
                                }}
                              />
                              <InputText
                                value={point?.value ?? ''}
                                className="w-full"
                                placeholder="Уровень напряжения, кВт"
                                maxLength={9}
                                onChange={(e) => {
                                  const updated = [...(field.state.value ?? [])];
                                  updated[index] = { ...updated[index], value: e.target.value };
                                  field.handleChange(updated);
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </Field>
                  ) : null}
                </div>
              ) : null}

              {shouldShow(type, 2, 3, 4) ? (
                <div className="flex flex-column gap-2">
                  <span className="font-semibold">Ранее присоединенная мощность</span>
                  <div className="flex flex-column md:flex-row gap-2">
                    <div className="flex-1">
                      <Field
                        form={form}
                        name="powerBefore"
                        validators={{
                          onChange: ({value}) => maxLength(value, 9),
                        }}
                      >
                        {(field) => (
                          <div className="flex flex-column gap-2">
                            <label htmlFor={field.name}>Ранее присоединенная мощность, кВт</label>
                            <InputText
                              id={field.name}
                              value={field.state.value ?? ''}
                              onBlur={field.handleBlur}
                              className="w-full"
                              maxLength={9}
                              onChange={(e) => field.handleChange(e.target.value)}
                            />
                            {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                              <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                            ) : null}
                          </div>
                        )}
                      </Field>
                    </div>
                    <div className="flex-1">
                      <Field
                        form={form}
                        name="voltage3"
                        validators={{
                          onChange: ({value}) => maxLength(value, 9),
                        }}
                      >
                        {(field) => (
                          <div className="flex flex-column gap-2">
                            <label htmlFor={field.name}>Уровень напряжения, кВ</label>
                            <InputText
                              id={field.name}
                              value={field.state.value ?? ''}
                              onBlur={field.handleBlur}
                              className="w-full"
                              maxLength={9}
                              onChange={(e) => field.handleChange(e.target.value)}
                            />
                            {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                              <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                            ) : null}
                          </div>
                        )}
                      </Field>
                    </div>
                  </div>

                  {shouldShow(type, 2) ? (
                    <Field form={form} name="pointsB">
                      {(field) => (
                        <div className="flex flex-column gap-2">
                          <div className="flex align-items-center justify-content-between">
                            <span>Точки присоединения</span>
                            <Button
                              type="button"
                              size="small"
                              onClick={() =>
                                field.handleChange([...(field.state.value ?? []), {name: '', value: ''}])
                              }
                            >
                              Добавить точку
                            </Button>
                          </div>
                          <small className="text-color-secondary">
                            Количество точек присоединения с указанием технических параметров элементов
                            энергопринимающих устройств
                          </small>
                          {(field.state.value ?? []).map((point, index) => (
                            <div key={index} className="flex flex-column md:flex-row gap-2">
                              <InputText
                                value={point?.name ?? ''}
                                className="w-full"
                                placeholder="Наименование"
                                maxLength={99}
                                onChange={(e) => {
                                  const updated = [...(field.state.value ?? [])];
                                  updated[index] = { ...updated[index], name: e.target.value };
                                  field.handleChange(updated);
                                }}
                              />
                              <InputText
                                value={point?.value ?? ''}
                                className="w-full"
                                placeholder="Уровень напряжения, кВт"
                                maxLength={9}
                                onChange={(e) => {
                                  const updated = [...(field.state.value ?? [])];
                                  updated[index] = { ...updated[index], value: e.target.value };
                                  field.handleChange(updated);
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </Field>
                  ) : null}
                </div>
              ) : null}

              {shouldShow(type, 1) ? (
                <div className="flex flex-column gap-2">
                  <Field
                    form={form}
                    name="tempDates"
                    validators={{
                      onChange: ({value}) => maxLength(value, 99),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>Срок электроснабжения по временной схеме</label>
                        <InputText
                          id={field.name}
                          value={field.state.value ?? ''}
                          onBlur={field.handleBlur}
                          className="w-full"
                          maxLength={99}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                          <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field
                    form={form}
                    name="contractInfo"
                    validators={{
                      onChange: ({value}) => maxLength(value, 99),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>Реквизиты договора на технологическое присоединение</label>
                        <InputText
                          id={field.name}
                          value={field.state.value ?? ''}
                          onBlur={field.handleBlur}
                          className="w-full"
                          maxLength={99}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        <small className="text-color-secondary">
                          Информация о реквизитах договора об осуществлении технологического присоединения
                        </small>
                        {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                          <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                </div>
              ) : null}

              {shouldShow(type, 2, 3, 4) ? (
                <div className="flex flex-column gap-2">
                  <Field
                    form={form}
                    name="loadNature"
                    validators={{
                      onChange: ({value}) => (value ? undefined : undefined),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>Характер нагрузки</label>
                        <Dropdown
                          showClear={isDraft}
                          inputId={field.name}
                          value={field.state.value ?? null}
                          options={CONNECTION_LOAD_NATURE_OPTIONS}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Выберите характер нагрузки"
                          className="w-full"
                          pt={{
                            input: {
                              className: styles.dropdownEllipsisInput,
                            },
                          }}
                          onChange={(e) => field.handleChange(e.value ?? undefined)}
                          onBlur={field.handleBlur}
                        />
                      </div>
                    )}
                  </Field>
                  <Field
                    form={form}
                    name="loadNatureFull"
                    validators={{
                      onChange: ({value}) => maxLength(value, 199),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>Заявляемый характер нагрузки</label>
                        <InputText
                          id={field.name}
                          value={field.state.value ?? ''}
                          onBlur={field.handleBlur}
                          className="w-full"
                          maxLength={199}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        <small className="text-color-secondary">
                          Бытовая, промышленная, вид деятельности и искажающие нагрузку факторы
                        </small>
                        {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                          <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                </div>
              ) : null}

              {shouldShow(type, 2) ? (
                <div className="flex flex-column gap-2">
                  <Field form={form} name="transNum" validators={{ onChange: ({value}) => maxLength(value, 19) }}>
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>Количество и мощность трансформаторов</label>
                        <InputText
                          id={field.name}
                          value={field.state.value ?? ''}
                          onBlur={field.handleBlur}
                          className="w-full"
                          maxLength={19}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        <small className="text-color-secondary">
                          Количество и мощность присоединяемых к сети трансформаторов, кВт
                        </small>
                        {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                          <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field form={form} name="genNum" validators={{ onChange: ({value}) => maxLength(value, 19) }}>
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>Количество и мощность генераторов</label>
                        <InputText
                          id={field.name}
                          value={field.state.value ?? ''}
                          onBlur={field.handleBlur}
                          className="w-full"
                          maxLength={19}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        <small className="text-color-secondary">Количество и мощность генераторов</small>
                        {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                          <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field form={form} name="generator" validators={{ onChange: ({value}) => maxLength(value, 199) }}>
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>Технологический минимум</label>
                        <InputText
                          id={field.name}
                          value={field.state.value ?? ''}
                          onBlur={field.handleBlur}
                          className="w-full"
                          maxLength={199}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        <small className="text-color-secondary">
                          Величина и обоснование технологического минимума (для генераторов)
                        </small>
                        {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                          <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field form={form} name="armor" validators={{ onChange: ({value}) => maxLength(value, 199) }}>
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>Аварийная броня</label>
                        <InputText
                          id={field.name}
                          value={field.state.value ?? ''}
                          onBlur={field.handleBlur}
                          className="w-full"
                          maxLength={199}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        <small className="text-color-secondary">
                          Необходимость наличия технологической и (или) аварийной брони
                        </small>
                        {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                          <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field form={form} name="armorValue" validators={{ onChange: ({value}) => maxLength(value, 199) }}>
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>Величина аварийной брони</label>
                        <InputText
                          id={field.name}
                          value={field.state.value ?? ''}
                          onBlur={field.handleBlur}
                          className="w-full"
                          maxLength={199}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        <small className="text-color-secondary">
                          Величина и обоснование технологической и аварийной брони
                        </small>
                        {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                          <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                </div>
              ) : null}

              {shouldShow(type, 3, 4) ? (
                <Field
                  form={form}
                  name="reliability"
                >
                  {(field) => (
                    <div className="flex flex-column gap-2">
                      <label htmlFor={field.name} className={styles.requiredLabel}>
                        Заявленный уровень надежности {requiredMark}
                      </label>
                      <Dropdown
                        showClear={isDraft}
                        inputId={field.name}
                        value={field.state.value ?? null}
                        options={CONNECTION_RELIABILITY_OPTIONS}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Выберите уровень надежности"
                        className="w-full"
                        pt={{
                          input: {
                            className: styles.dropdownEllipsisInput,
                          },
                        }}
                        onChange={(e) => field.handleChange(e.value ?? undefined)}
                        onBlur={field.handleBlur}
                      />
                      {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                        <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                      ) : null}
                    </div>
                  )}
                </Field>
              ) : null}

              {shouldShow(type, 2) ? (
                <div className="flex flex-column gap-2">
                  <span className="font-semibold">Категории надежности</span>
                  <div className="flex flex-column md:flex-row gap-2">
                    {(['level1', 'level2', 'level3'] as const).map((level, idx) => (
                      <div key={level} className="flex-1">
                        <Field
                          form={form}
                          name={level}
                          validators={{
                            onChange: ({value}) => maxLength(value, 9),
                          }}
                        >
                          {(field) => (
                            <div className="flex flex-column gap-2">
                              <label htmlFor={field.name}>{`${idx + 1} Категория, кВт`}</label>
                              <InputText
                                id={field.name}
                                value={field.state.value ?? ''}
                                onBlur={field.handleBlur}
                                className="w-full"
                                maxLength={9}
                                onChange={(e) => field.handleChange(e.target.value)}
                              />
                              {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                                <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                              ) : null}
                            </div>
                          )}
                        </Field>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {shouldShow(type, 2, 3, 4) ? (
                <div className="flex flex-column gap-2">
                  <Field form={form} name="dates" validators={{ onChange: ({value}) => maxLength(value, 199) }}>
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>Сроки проектирования</label>
                        <InputText
                          id={field.name}
                          value={field.state.value ?? ''}
                          onBlur={field.handleBlur}
                          className="w-full"
                          maxLength={199}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        <small className="text-color-secondary">
                          Сроки проектирования и поэтапного введения в эксплуатацию
                        </small>
                        {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                          <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field form={form} name="plans" validators={{ onChange: ({value}) => maxLength(value, 199) }}>
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>Распределение, сроки и категория надежности</label>
                        <InputText
                          id={field.name}
                          value={field.state.value ?? ''}
                          onBlur={field.handleBlur}
                          className="w-full"
                          maxLength={199}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        <small className="text-color-secondary">
                          Планируемое распределение мощности и сведения о категории надежности
                        </small>
                        {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                          <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                </div>
              ) : null}

              {shouldShow(type, 3) ? (
                <Field form={form} name="offers" validators={{ onChange: ({value}) => maxLength(value, 199) }}>
                  {(field) => (
                    <div className="flex flex-column gap-2">
                      <label htmlFor={field.name}>Предложения по порядку расчетов</label>
                      <InputText
                        id={field.name}
                        value={field.state.value ?? ''}
                        onBlur={field.handleBlur}
                        className="w-full"
                        maxLength={199}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                      <small className="text-color-secondary">
                        Предложения по порядку расчетов и условиям рассрочки внесения платы
                      </small>
                      {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                        <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                      ) : null}
                    </div>
                  )}
                </Field>
              ) : null}

              <Field
                form={form}
                name="reason"
              >
                {(field) => (
                  <div className="flex flex-column gap-2">
                    <label htmlFor={field.name} className={styles.requiredLabel}>
                      Цель обращения {requiredMark}
                    </label>
                    <Dropdown
                      showClear={isDraft}
                      inputId={field.name}
                      value={field.state.value ?? null}
                      options={CONNECTION_REASON_OPTIONS}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Выберите цель обращения"
                      className="w-full"
                      pt={{
                        input: {
                          className: styles.dropdownEllipsisInput,
                        },
                      }}
                      onChange={(e) => field.handleChange(e.value ?? undefined)}
                      onBlur={field.handleBlur}
                    />
                    {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                      <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                    ) : null}
                  </div>
                )}
              </Field>

              <Field
                form={form}
                name="method"
              >
                {(field) => (
                  <div className="flex flex-column gap-2">
                    <label htmlFor={field.name} className={styles.requiredLabel}>
                      Способ передачи проекта договора и технических условий {requiredMark}
                    </label>
                    <Dropdown
                      showClear={isDraft}
                      inputId={field.name}
                      value={field.state.value ?? null}
                      options={CONNECTION_METHOD_OPTIONS}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Выберите способ передачи"
                      className="w-full"
                      pt={{
                        input: {
                          className: styles.dropdownEllipsisInput,
                        },
                      }}
                      onChange={(e) => field.handleChange(e.value ?? undefined)}
                      onBlur={field.handleBlur}
                    />
                    {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                      <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                    ) : null}
                  </div>
                )}
              </Field>

              <div className="flex flex-column gap-2">
                <span className="font-semibold">
                  Прилагаемые файлы (pdf, png, jpeg, jpg: не более 3 МБ каждый, не более 10 файлов на заявку)
                </span>
                {!isDraft && existingFiles.length ? (
                  <div className="flex flex-column gap-1">
                    <span className="text-sm font-medium">Загруженные ранее файлы:</span>
                    {existingFiles.map((file) => (
                      <Button
                        key={file.id}
                        type="button"
                        text
                        icon="pi pi-download"
                        label={file.name}
                        className="p-0 text-left"
                        onClick={() => downloadExistingFile(file)}
                      />
                    ))}
                  </div>
                ) : null}
                {isDraft ? (
                  <React.Fragment>
                    <span className={`${styles.requiredLabel} font-semibold`}>
                      Паспорт / Доверенность или иные документы {requiredMark}
                    </span>
                    {renderFileUpload('routes', 'Выбрать файлы')}
                    {renderFileError('routes')}
                    <small className="text-color-secondary">
                      Паспорт (главный разворот и разворот с пропиской) / Доверенность или иные документы, подтверждающие полномочия представителя заявителя, подающего и получающего документы, в случае подачи заявки представителем заявителя, а также документ подтверждающий полномочия руководителя либо лица, уполномоченного на подписание договора
                    </small>
                    <span className={`${styles.requiredLabel} font-semibold`}>
                      Право собственности на присоединяемый объект {requiredMark}
                    </span>
                    {renderFileUpload('ownership', 'Выбрать файлы')}
                    {renderFileError('ownership')}
                    <small className="text-color-secondary">
                      Документы, подтверждающие право собственности или иное предусмотренное законом основание на объект капитального строительства (нежилое помещение в таком объекте капитального строительства) и (или) земельный участок, на котором расположены (будут располагаться) объекты заявителя, либо право собственности или иное предусмотренное законом основание на энергопринимающие устройства (для заявителей, планирующих осуществить технологическое присоединение энергопринимающих устройств потребителей, расположенных в нежилых помещениях многоквартирных домов или иных объектах капитального строительства, - Копия документа, подтверждающего право собственности или иное предусмотренное законом основание на нежилое помещение в таком многоквартирном доме или ином объекте капитального строительства)
                    </small>
                    {shouldShow(type, 2, 3, 4) ? (
                      <div className="flex flex-column gap-2">
                        {renderFileUpload('plan', 'План расположения')}
                        {renderFileError('plan')}
                        <small className="text-color-secondary">
                          План расположения энергопринимающего устройства, присоединяемого к сетям сетевой организации
                        </small>
                        {renderFileUpload('energySchema', 'Однолинейная схема')}
                        {renderFileError('energySchema')}
                        <small className="text-color-secondary">
                          Однолинейная схема электрических сетей присоединяемых к электрическим сетям сетевой организации, номинальный класс напряжения которых составляет 35 кВ и выше, с указанием возможности резервирования от собственных источников энергоснабжения (включая резервирование для собственных нужд) и возможности переключения нагрузок (генерации) по внутренним сетям заявителя
                        </small>
                      </div>
                    ) : null}

                    {shouldShow(type, 2, 3, 4) ? (
                      <div className="flex flex-column gap-2">
                        {renderFileUpload('ukAgreement', 'Разрешение управляющей компании')}
                        {renderFileError('ukAgreement')}
                        <small className="text-color-secondary">
                          Документы подтверждающие согласие организации, осуществляющей управление многоквартирным домом, при наличии у такой организации соответствующих полномочий либо при ее отсутствии или отсутствии у нее полномочий согласие общего собрания владельцев жилых помещений многоквартирного дома на организацию присоединения нежилого помещения отдельными линиями от вводного устройства (вводно-распределительного устройства, главного распределительного щита), установленного на вводе питающей линии сетевой организации в соответствующее здание или его обособленную часть (если для соответствующего нежилого помещения проектом на многоквартирный дом не предусмотрено индивидуальное вводно-распределительное устройство с непосредственным присоединением к питающей линии сетевой организации)
                        </small>
                      </div>
                    ) : null}

                    {shouldShow(type, 1) ? (
                      <div className="flex flex-column gap-2">
                        {renderFileUpload('contractInfoFile', 'Информация о реквизитах договора')}
                        {renderFileError('contractInfoFile')}
                        <small className="text-color-secondary">
                          Информация о реквизитах договора об осуществлении технологического присоединения (за исключением заявителей, энергопринимающие устройства которых являются передвижными и имеют максимальную мощность до 150 кВт включительно)
                        </small>
                      </div>
                    ) : null}

                    {renderFileUpload('misc', 'Прочий документ')}
                    {renderFileError('misc')}
                  </React.Fragment>
                ) : null}
              </div>

              <Field
                form={form}
                name="personalAgreement"
              >
                {(field) => (
                  <div className="flex align-items-center gap-2">
                    <Checkbox
                      inputId={field.name}
                      checked={field.state.value ?? false}
                      onChange={(e) => field.handleChange(e.checked ?? false)}
                    />
                    <label htmlFor={field.name} className={styles.requiredLabel}>
                      Я согласен на передачу своих персональных данных Сетевой организации и Гарантирующему поставщику
                      {' '}
                      {requiredMark}
                    </label>
                    {fieldErrors[field.name] || (field.state.meta.isTouched && field.state.meta.errors[0]) ? (
                      <small className="p-error">{fieldErrors[field.name] ?? field.state.meta.errors[0]}</small>
                    ) : null}
                  </div>
                )}
              </Field>
              </React.Fragment>
            );
          }}
        </Subscribe>

        <Subscribe<number> selector={(state) => state.values.type}>
          {(type) => isDraft ? (
            <div className="flex flex-column gap-2">
              {FILE_KEYS.filter((key) => !type || !visibleFileKeys(type).includes(key)).filter((key) =>
                !type || (fileGroups[key]?.length ?? 0) > 0 || (files[key]?.length ?? 0) > 0,
              ).map((key) => (
                <div key={key} className="flex flex-column gap-2">
                  <span className="font-semibold">{FILE_LABELS[key]}</span>
                  {renderFileUpload(key, 'Выбрать файлы')}
                  {renderFileError(key)}
                </div>
              ))}
            </div>
          ) : null}
        </Subscribe>
        {Object.keys(fieldErrors).length || Object.keys(fileErrors).length ? (
          <div role="alert" className="p-error">
            <div>Проверьте поля заявления:</div>
            <ul>
              {Object.entries(fieldErrors).map(([key, message]) => <li key={key}><a href={`#${key}`}>{FIELD_LABELS[key] ?? key}: {message}</a></li>)}
              {Object.entries(fileErrors).map(([key, message]) => <li key={key}><a href={`#files-${key}`}>{FILE_LABELS[key as keyof FileGroups] ?? key}: {message}</a></li>)}
            </ul>
          </div>
        ) : null}
            <div className="flex flex-column gap-2">
              <span className="text-sm text-color-secondary">
                Обязательные поля нужно заполнить перед отправкой заявления.
              </span>
              <div className="flex flex-wrap justify-content-start gap-2">
              {isDraft ? <Button type="button" outlined label="Сохранить черновик" disabled={isLoading || isUploadingFiles}
                loading={isLoading} onClick={() => void save('draft')} /> : null}
              <Button
                type="submit"
                disabled={isLoading || isUploadingFiles}
                loading={isLoading || isUploadingFiles}
              >
                {isDraft ? 'Отправить заявление' : 'Обновить'}
              </Button>
              </div>
            </div>
          </div>
        </fieldset>
      </div>
    </form>
  );
};
